import { mapValues } from 'es-toolkit';

import { BearingEnvelope, FeaturePatternTrussContainer, TrussEnvelope, WorkLine } from '@paragon/contract-library';
import { Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';
import {
  calculateBearingEnvelopeShape,
  calculateFloorPolyline2DShape,
  calculateTrussEnvelopeShapeWithEdgeSegments,
  calculateTrussSolidWithCache,
  TrussSolidCalculationDependencies,
} from '@paragon/layout';
import { assertExhaustiveSwitch, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { FundamentalLayoutData } from '../../../store/selectors/layoutDataSelectors';
import { FocusedFeaturePatternContainerData } from '../../../store/selectors/layoutGeometricEcosystemSelectors';
import { RoofContainerBasedGeometricEcosystem } from './RoofContainerBasedGeometricEcosystem';
import { RoofPlaneBasedGeometricEcosystem } from './RoofPlaneBasedGeometricEcosystem';
import { TrussEnvelopeData } from './TrussEnvelopeData';

/*
 * The functions in this file are intended for use by `GeometricEcosystem` implementations to prevent boilerplate and
 *   share common code between them, which is why they all take a `FundamentalLayoutData` even though that's overkill in
 *   many cases. Feel free to adopt them for general use if you want, but that is not their current purpose.
 */

export function getBearingAreas(data: FundamentalLayoutData): Record<string, Polygon2D> {
  const result: Record<string, Polygon2D> = {};
  for (const bearingEnvelope of Object.values(data.bearingEnvelopes)) {
    const shape = calculateBearingEnvelopeShape(bearingEnvelope);
    if (shape) {
      result[bearingEnvelope.guid] = shape;
    }
  }

  return result;
}

export function getWorkLineGeometries(data: FundamentalLayoutData): Record<string, Line2D | LineSegment2D> {
  return mapValues(data.workLines, (workLine) => {
    switch (workLine.geometryType) {
      case 'Line':
        return Line2D.fromObject(workLine.geometry);

      case 'Segment':
        return LineSegment2D.fromLineSegmentObject(workLine.geometry);

      default:
        assertExhaustiveSwitch(workLine);
    }
  });
}

/** Only one building is visible at a time, so all visible building-scoped work lines are included. */
export function doesWorkLineBoundLevel(workLine: WorkLine, levelGuid: string | null) {
  return workLine.scope.type !== 'Level' || workLine.scope.levelGuid === levelGuid;
}

export function getBoundingWorkLineGuids(data: FundamentalLayoutData): Set<string> {
  return new Set<string>(
    Object.values(data.workLines)
      .filter((workLine) => workLine.bounding)
      .map((workLine) => workLine.guid),
  );
}

export function getTrussEnvelopeData(
  trussEnvelopes: ReadonlyRecord<string, TrussEnvelope>,
  dependencies: TrussSolidCalculationDependencies,
) {
  return Object.fromEntries(
    Object.values(trussEnvelopes)
      .map((envelope) => {
        const shapeWithEdgeSegments = calculateTrussEnvelopeShapeWithEdgeSegments(envelope);
        if (!shapeWithEdgeSegments) return null;

        const { shape, backSegment, frontSegment, middleSegment } = shapeWithEdgeSegments;

        const nonSolidProperties: Omit<TrussEnvelopeData, 'solid'> = {
          envelope,
          shape,

          backSegmentWithBevelsConsidered: backSegment,
          frontSegmentWithBevelsConsidered: frontSegment,
          middleSegmentWithBevelsConsidered: middleSegment,
        };

        return { ...nonSolidProperties, solid: calculateTrussSolidWithCache(envelope, dependencies).ok ?? null };
      })
      .filter(notNullish)
      .map((data) => [data.envelope.guid, data] as const),
  );
}

export function getFeaturePatternContainerData(
  featurePatternContainers: ReadonlyRecord<string, FeaturePatternTrussContainer>,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
) {
  return Object.fromEntries(
    Object.values(featurePatternContainers)
      .map((container) => [container.guid, calculateFloorPolyline2DShape(container.shape, bearingEnvelopes)] as const)
      .map(([guid, boundedData]) => boundedData && ([guid, boundedData] as const))
      .filter(notNullish),
  );
}

export function setFocusedObjectDerivedData(
  data: FocusedFeaturePatternContainerData,
  allBearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
  ecosystem: RoofContainerBasedGeometricEcosystem | RoofPlaneBasedGeometricEcosystem,
) {
  // Feature pattern containers are the only type of object that can be snapped to while still being inserted, because
  //   of the need to support selecting the edge to slope from.

  if (data) {
    const boundedData = calculateFloorPolyline2DShape(data, allBearingEnvelopes);

    if (boundedData) {
      ecosystem.focusedFeaturePatternContainer = boundedData;
    }
  }

  if (!ecosystem.focusedFeaturePatternContainer) {
    ecosystem.focusedFeaturePatternContainer = null;
  }
}
